import type {
  BatchDeclarationGate,
  ExternalToolBatch,
  ToolCallExpectedGates,
} from './external-tool-batch.types';
import {
  isBatchDeclarationAborted,
  isBatchDeclarationTimedOut,
  isToolCallExpectedAborted,
  isToolCallExpectedTimedOut,
  releaseBatchDeclaration,
  releaseToolCallExpected,
} from './external-tool-batch-waiters';

export function declareExternalToolBatchAliases({
  batch,
  aliasIds,
  toolCallIds,
  toolCallExpectedGates,
  batchDeclarationGates,
}: {
  batch: ExternalToolBatch;
  aliasIds: string[];
  toolCallIds: string[];
  toolCallExpectedGates: ToolCallExpectedGates;
  batchDeclarationGates: Map<string, BatchDeclarationGate>;
}): string[] {
  const declarationTimedOut = aliasIds.some((aliasId) =>
    isBatchDeclarationTimedOut({
      gates: batchDeclarationGates,
      batchId: aliasId,
    }),
  );

  if (declarationTimedOut) {
    return [];
  }

  const declarationAborted = aliasIds.some((aliasId) =>
    isBatchDeclarationAborted({
      gates: batchDeclarationGates,
      batchId: aliasId,
    }),
  );

  if (!declarationAborted) {
    batch.declared = true;
  }

  const declaredToolCallIds: string[] = [];

  for (const toolCallId of toolCallIds) {
    const expectedTimedOut = aliasIds.some((aliasId) =>
      isToolCallExpectedTimedOut({
        gates: toolCallExpectedGates,
        batchId: aliasId,
        toolCallId,
      }),
    );
    const expectedAborted = aliasIds.some((aliasId) =>
      isToolCallExpectedAborted({
        gates: toolCallExpectedGates,
        batchId: aliasId,
        toolCallId,
      }),
    );

    if (expectedTimedOut) {
      continue;
    }

    if (!declarationAborted) {
      batch.expectedToolCallIds.add(toolCallId);
    }

    if (declarationAborted || expectedAborted) {
      batch.pendingExecuteToolCallIds.delete(toolCallId);
      continue;
    }

    declaredToolCallIds.push(toolCallId);

    for (const aliasId of aliasIds) {
      releaseToolCallExpected({
        gates: toolCallExpectedGates,
        batchId: aliasId,
        toolCallId,
      });
    }
  }

  if (declarationAborted) {
    return [];
  }

  for (const aliasId of aliasIds) {
    releaseBatchDeclaration({ gates: batchDeclarationGates, batchId: aliasId });
  }

  return declaredToolCallIds;
}
